// TODO: properly type this file
/* eslint-disable @typescript-eslint/no-explicit-any */

import { t } from "i18next";

import { readState } from "@/__main__/app-state.mjs";
import { appURLs, I18N_PLACEHOLDER } from "@/app/constants.mjs";
import { getRitoLanguageCodeFromBCP47 } from "@/app/util.mjs";
import {
  POTIONS,
  TRINKETS,
  WARDS,
} from "@/game-lol/constants/builds-constants.mjs";
import {
  CHAMPION_ABILITY_MAP,
  CHAMPION_PROFICIENCY,
  FLASH_PLACEMENT_NULL,
  IS_APEX,
  MAP_ID,
  MAX_GAME_TIME_IN_MINUTES,
  NONSCORABLE_QUEUE_TYPES,
  QUEUE_SYMBOLS,
  RANK_SYMBOL_TO_STR,
  RANK_SYMBOLS,
  RANK_TIERS,
  RANK_VALS,
  REGION_LIST,
  REGION_SYMBOL_BR,
  REGION_SYMBOL_EUNE,
  REGION_SYMBOL_EUW,
  REGION_SYMBOL_JP,
  REGION_SYMBOL_KR,
  REGION_SYMBOL_LAN,
  REGION_SYMBOL_LAS,
  REGION_SYMBOL_MEA,
  REGION_SYMBOL_NA,
  REGION_SYMBOL_OCE,
  REGION_SYMBOL_PH,
  REGION_SYMBOL_RU,
  REGION_SYMBOL_SG,
  REGION_SYMBOL_TH,
  REGION_SYMBOL_TR,
  REGION_SYMBOL_TW,
  REGION_SYMBOL_VN,
  ROLE_PAIRS,
  ROLE_SYMBOL_TO_STR,
  ROLE_SYMBOLS,
  SCORABLE_QUEUE_TYPES,
  SEASONS_START,
  shardMap,
  SKILL_HOTKEYS,
  SUPPORT_ITEM,
} from "@/game-lol/constants/constants.mjs";
import lolRefs from "@/game-lol/refs.mjs";
import {
  isDerivedRiotID,
  separateDerivedRiotID,
} from "@/game-lol/utils/derived-id.mjs";
import { getCurrentPatchForStaticData } from "@/game-lol/utils/patches.mjs";
import RuneTooltip from "@/game-lol/utils/rune-tooltip.mjs";
import Static from "@/game-lol/utils/static.mjs";
import { getStaticData } from "@/game-lol/utils/static-data.mjs";
import QueueSymbol, {
  getQueueDetails,
  QUEUE_SYMBOL_TO_OBJECT,
} from "@/game-lol/utils/symbol-queue.mjs";
import { RankSymbol } from "@/game-lol/utils/symbol-rank.mjs";
import RoleSymbol from "@/game-lol/utils/symbol-role.mjs";
import { calcRate } from "@/util/helpers.mjs";
import { getLocale } from "@/util/i18n-helper.mjs";
import parseSearchParams from "@/util/parse-search-params.mjs";
import { useSnapshot } from "@/util/use-snapshot.mjs";

export * from "@/game-lol/utils/colors.mjs";
export * from "@/game-lol/utils/derived-id.mjs";
export * from "@/game-lol/utils/patches.mjs";
export * from "@/game-lol/utils/roles.mjs";
export * from "@/game-lol/utils/static-data.mjs";

export const pretty = (s) =>
  s.replace(/[ /]/g, "-").replace(/[+]/g, "_plus").toLowerCase(); // aka: 'Ranked Solo/Duo' -> 'ranked-solo-duo'

export function prettyPatch(patch) {
  if (!patch) return I18N_PLACEHOLDER;
  const [major, minor] = (patch ?? "").split(".");
  return `${major}.${minor}`;
}

export const defaultRole = "ALL";
export const defaultRank = "PLATINUM_PLUS";
export const defaultRegion = "WORLD";
export const defaultGameMode = "RANKED_SOLO_5X5"; // ranked-solo-duo
export const defaultSynergiesGameMode = "RANKED_SOLO_5X5"; // ranked-solo-duo

export function useChampionsStatsPatch() {
  const state = useSnapshot(readState);
  // @ts-ignore
  return state.lol.championStatsPatch;
}

export function useChampionsSynergiesPatch() {
  const state = useSnapshot(readState);
  // @ts-ignore
  return state.lol.championSynergiesPatch;
}

export function parseRawChampionName(rawChampionName) {
  if (!rawChampionName) return null;

  const parts = rawChampionName.split("_");
  return parts.length === 4 ? parts[3] : parts.length === 3 ? parts[1] : null;
}

export function getRankStatsAPIParams({ role, championId, isARAM }) {
  const variables = isARAM
    ? {
        championId: championId,
        queue: getQueueDetails(QUEUE_SYMBOLS.aram).gql,
      }
    : {
        role: ROLE_SYMBOL_TO_STR[RoleSymbol(role)]?.gql,
        championId: championId,
        queue: getQueueDetails(QUEUE_SYMBOLS.rankedSoloDuo).gql,
      };
  return variables;
}

export function getChampionDivStatsId({ championId, role, queue }) {
  return `${queue}-${championId}-${role || ""}`;
}

export function coachingStatsVariables({
  queue,
  tier,
  championId,
  role,
}: {
  queue: string | symbol;
  tier?: string | symbol;
  championId: number;
  role?: string | symbol;
}): {
  championId: number;
  queue: string | symbol;
  tier?: string | symbol;
  role?: string | symbol;
} {
  const vars = { championId } as any;

  if (typeof tier === "string") vars.tier = tier;
  else if (typeof tier === "symbol") vars.tier = RANK_SYMBOL_TO_STR[tier].gql;
  if (typeof role === "string") vars.role = role;
  else if (typeof role === "symbol") vars.role = ROLE_SYMBOL_TO_STR[role].gql;

  if (isARAMQueue(QueueSymbol(queue))) {
    delete vars.role;
    delete vars.tier;
    vars.queue = QUEUE_SYMBOL_TO_OBJECT[QUEUE_SYMBOLS.aram].gql;
  } else {
    vars.queue = QUEUE_SYMBOL_TO_OBJECT[QUEUE_SYMBOLS.rankedSoloDuo].gql;
  }

  return vars;
}

export function coachingStatsKey({
  queue,
  tier,
  championId,
  role,
}: {
  queue: string | symbol;
  tier?: string | symbol;
  championId: number;
  role?: string | symbol;
}): string {
  const params = { championId } as any;

  let key = `${params.championId}`;

  if (typeof queue === "string") key += `-${queue}`;
  else if (typeof queue === "symbol")
    key += `-${QUEUE_SYMBOL_TO_OBJECT[queue].gql}`;
  if (typeof tier === "string") key += `-${tier}`;
  else if (typeof tier === "symbol") key += `-${RANK_SYMBOL_TO_STR[tier].gql}`;
  if (typeof role === "string") key += `-${role}`;
  else if (typeof role === "symbol") key += `-${ROLE_SYMBOL_TO_STR[role].gql}`;

  return key;
}

export function getDerivedQueue(derivedId, queue) {
  if (!queue) queue = getQueueDetails(QUEUE_SYMBOLS.rankedSoloDuo).gql;
  if (typeof queue === "symbol") queue = getQueueDetails(queue).gql;
  return `${derivedId}_${queue}`.toLowerCase();
}

export const getLPColor = (lp) => {
  return lp > 0 ? "var(--turq)" : lp < 0 ? "var(--red)" : "var(--shade2)";
};

export const getLPString = (lp, t) => {
  const lpSign = lp > 0 ? "+" : "";
  return `${lpSign}${t("lol:leaguePoints", "{{points}} LP", {
    points: lp,
  })}`;
};

export const findLeagueDataFromQueue = (leagues, queue) =>
  (Array.isArray(leagues) ? leagues : []).find(
    (l) => QueueSymbol(l.queue) === QueueSymbol(queue),
  );

// Queue ::WITH:: a role for the purposes of builds
export const isQueueWithRoles = (queue) => {
  if (isARAMQueue(queue)) {
    return false;
  }
  return SCORABLE_QUEUE_TYPES.includes(QueueSymbol(queue));
};

// Queue ::WITHOUT:: a role for the purposes of builds
export const isQueueWithoutRoles = (queue) => {
  if (isARAMQueue(queue) || isARAMMayhemQueue(queue)) {
    return true;
  }
  return NONSCORABLE_QUEUE_TYPES.includes(QueueSymbol(queue));
};

export const isTFTQueue = (mapId: number): boolean => {
  return mapId === 22;
};

export const isSRQueue = (queue, gameMode, mapId): boolean => {
  if (gameMode && mapId) {
    return gameMode === "CLASSIC" && mapId === 11;
  }
  return SCORABLE_QUEUE_TYPES.includes(QueueSymbol(queue));
};

export const isARAMQueue = (queue): boolean => {
  // No longer only Howling Abyss map
  return (
    QueueSymbol(queue) === QUEUE_SYMBOLS.aram ||
    QueueSymbol(queue) === QUEUE_SYMBOLS.clashAram
  );
};

export const isARAMMayhemQueue = (queue): boolean => {
  return (
    queue === "KIWI" ||
    queue === "2400" ||
    queue === 2400 ||
    queue === "3270" ||
    queue === 3270 ||
    QueueSymbol(queue) === QUEUE_SYMBOLS.aramMayhem
  );
};

export const isArenaQueue = (queue) => {
  if (queue === "CHERRY") return true;
  return QueueSymbol(queue) === QUEUE_SYMBOLS.arena;
};

export const isBrawlQueue = (queue) => {
  if (queue === "BRAWL") return true;
  return QueueSymbol(queue) === QUEUE_SYMBOLS.brawl;
};

export const isDoomBotsQueue = (queue) => {
  const queueTypes = ["RUBY", "RUBY_TRIAL_1", "RUBY_TRIAL_2", "RUBY_TRIAL_3"];
  if (queueTypes.includes(queue)) return true;
  return (
    QueueSymbol(queue) === QUEUE_SYMBOLS.doomBots ||
    QueueSymbol(queue) === QUEUE_SYMBOLS.trialsOfDoom
  );
};

export const isSwarmQueue = (queue, map?: number): boolean => {
  if (map === MAP_ID.SWARM) {
    return true;
  }

  return QueueSymbol(queue) === QUEUE_SYMBOLS.swarm;
};

export const isNexusBlitzQueue = (queue) => {
  return QueueSymbol(queue) === QUEUE_SYMBOLS.nexusBlitz;
};

export const isSwiftplayQueue = (queue) => {
  return QueueSymbol(queue) === QUEUE_SYMBOLS.swiftplay;
};

export const isOneForAllQueue = (queueId) => {
  return (
    `${queueId}` === QUEUE_SYMBOL_TO_OBJECT[QUEUE_SYMBOLS.oneForAll].key[0]
  );
};

export const isURFQueue = (queue) => {
  return (
    QueueSymbol(queue) === QUEUE_SYMBOLS.urf ||
    QueueSymbol(queue) === QUEUE_SYMBOLS.clashUrf
  );
};

export const isUltimateSpellbookQueue = (queue) => {
  return QueueSymbol(queue) === QUEUE_SYMBOLS.ultimateSpellbook;
};

export const isFlexQueue = (queue) =>
  QueueSymbol(queue) === QUEUE_SYMBOLS.rankedFlex;

export const isRoleQueue = (queue) => {
  return !QUEUE_SYMBOL_TO_OBJECT[QueueSymbol(queue)]?.hideRole;
};

export const isRankedQueue = (queue) => {
  const queueSymbol = QueueSymbol(queue);

  return (
    queueSymbol === QUEUE_SYMBOLS.rankedSoloDuo ||
    queueSymbol === QUEUE_SYMBOLS.rankedFlex
  );
};

export const isCoopVsAIQueue = (queue) => {
  const queueSymbol = QueueSymbol(queue);
  return (
    queueSymbol === QUEUE_SYMBOLS.coopVsAiIntro ||
    queueSymbol === QUEUE_SYMBOLS.coopVsAiBeginner ||
    queueSymbol === QUEUE_SYMBOLS.coopVsAiIntermediate
  );
};

export const isLoadingScreenQueue = (queue) => {
  const queueSymbol = QueueSymbol(queue);
  return (
    queueSymbol === QUEUE_SYMBOLS.rankedSoloDuo ||
    queueSymbol === QUEUE_SYMBOLS.rankedFlex ||
    queueSymbol === QUEUE_SYMBOLS.normalDraft ||
    queueSymbol === QUEUE_SYMBOLS.aram ||
    queueSymbol === QUEUE_SYMBOLS.aramMayhem ||
    queueSymbol === QUEUE_SYMBOLS.swiftplay ||
    queueSymbol === QUEUE_SYMBOLS.brawl
  );
};

const TAB_QUEUES = {
  aram: QUEUE_SYMBOLS.aram,
  "aram-mayhem": QUEUE_SYMBOLS.aramMayhem,
  arena: QUEUE_SYMBOLS.arena,
  "arena-duos": QUEUE_SYMBOLS.arena,
  "nexus-blitz": QUEUE_SYMBOLS.nexusBlitz,
  "one-for-all": QUEUE_SYMBOLS.oneForAll,
  urf: QUEUE_SYMBOLS.urf,
  doombots: QUEUE_SYMBOLS.doomBots,
};

type ChampionsFilters = {
  queue: string;
  region: string;
  comingSoon: boolean;
  patch: string;
  tier: string;
  mastery: string;
  role: string | null;
  duoRole?: string | null;
  championId?: number;
};

export const getDefaultedFiltersForChampions = (
  searchParams,
  tab,
  championId,
): ChampionsFilters => {
  const filters: ChampionsFilters = {
    queue: "",
    region: "",
    comingSoon: false,
    patch: "",
    tier: "",
    mastery: "1",
    role: null,
  };

  const queue: string | null = searchParams?.get("queue");
  const patch: string | null = searchParams?.get("patch");
  const region: string | null = searchParams?.get("region");
  const role: string | null = searchParams?.get("role");
  const duoRole: string | null = searchParams?.get("duoRole");
  const tier: string =
    searchParams?.get("tier") ||
    RANK_SYMBOL_TO_STR[RANK_SYMBOLS.emeraldPlus].gql;
  const mastery: string = searchParams?.get("mastery") || "1";

  const queueSymbol = queue
    ? QueueSymbol(queue)
    : TAB_QUEUES[tab] || QUEUE_SYMBOLS.rankedSoloDuo;
  const queueDetails = getQueueDetails(queueSymbol);

  const isSynergies = tab === "synergies";

  filters.queue = queueDetails.gql;
  filters.region = region || REGION_LIST[0].datalake;
  filters.comingSoon = queueDetails.comingSoon;

  if (patch) filters.patch = patch;

  if (role && championId) {
    filters.role = role;
  }

  if (championId !== undefined && championId !== null) {
    filters.championId = Number(championId);
  }

  if (isSynergies) {
    // Synergies requires 2 roles, ex: ADC and SUPPORT
    const defaultedRole = isSynergies
      ? role || ROLE_SYMBOL_TO_STR[ROLE_SYMBOLS.adc].gql
      : role || ROLE_SYMBOL_TO_STR[ROLE_SYMBOLS.all].gql;
    const defaultedduoRole = isSynergies
      ? duoRole ||
        ROLE_SYMBOL_TO_STR[ROLE_PAIRS[RoleSymbol(defaultedRole)]]?.gql
      : undefined;

    filters.tier = tier;
    filters.role = defaultedRole;
    filters.duoRole = defaultedduoRole;
  }

  if (isRankedQueue(queueSymbol)) {
    filters.tier = tier;
    filters.mastery = mastery;
  }

  return filters;
};

export const getSearchParamsForChampions = (
  isSynergiesFilter,
  filterParams = {},
) => {
  // @ts-ignore
  const { role, duoRole, tier, queue, region, patch, championId } =
    filterParams;

  const urlParams = {
    queue,
  } as any;

  if (patch) urlParams.patch = patch;

  if (isSynergiesFilter) {
    urlParams.role = role;
    urlParams.duoRole = duoRole;
    urlParams.tier = tier;
    urlParams.region = region;
  } else if (isARAMQueue(queue) || isDoomBotsQueue(queue)) {
    urlParams.region = region;
  } else if (!isRankedQueue(queue)) {
    urlParams.region = region;
  } else {
    urlParams.tier = tier;
    urlParams.region = region;
  }

  if (role && championId) urlParams.role = role;

  return parseSearchParams(urlParams);
};

export const durationToMinutes = (duration) => {
  if (!duration) return 1;
  if (duration / 60 > MAX_GAME_TIME_IN_MINUTES) {
    return duration / 60000;
  }
  return duration / 60;
};

/**
 * Returns if team won or not
 *
 * @param {Object} team
 * @returns {Boolean}
 */
export const isWinningTeam = (team) => {
  const { win, isWinningTeam } = team;
  const val = isWinningTeam || win;

  if (val === "Win") return true;

  return Boolean(val);
};

export const getOneRankAbove = (newRank) => {
  let oneRankAboveIndex =
    RANK_TIERS.findIndex((rank) => RankSymbol(rank) === newRank) + 1;
  if (oneRankAboveIndex === RANK_TIERS.length)
    oneRankAboveIndex = oneRankAboveIndex - 1;
  if (oneRankAboveIndex) return RANK_TIERS[oneRankAboveIndex];
};

export const getDefaultedFiltersForProBuilds = (searchParams) => {
  let role = searchParams?.get("role");
  const team = searchParams?.get("team");

  if (role === "null") role = null;

  return {
    role: role || ROLE_SYMBOL_TO_STR[ROLE_SYMBOLS.all].gql,
    team,
  };
};

export const getStaticChampionById = (
  championId?: number | string | null,
  patch?: string | undefined,
) => {
  const champions = getStaticData("champions", patch);
  if (!championId || !champions) return null;

  const championKey = champions?.keys?.[championId];
  const champion = champions?.[championKey];
  return champion
    ? {
        ...champion,
        id: champion?.key,
        key: championKey,
      }
    : undefined;
};

export const getStaticSpellByDisplayName = (longSpellName, patch) => {
  // checks the display name from liveClient to get spell ID
  // example name: "GeneratedTip_SummonerSpell_SummonerSnowball_DisplayName"
  // spell keys usually look like: "SummonerSnowball"
  if (!longSpellName) return null;
  const spells = getStaticData("spells", patch);
  const spellKey = Object.keys(spells).find((key) =>
    longSpellName.includes(key),
  );

  if (!spellKey) {
    /* special Bridge of Progress snowballs */
    const bridgeOfProgressSpells = [
      "GeneratedTip_SummonerSpell_Crepe_Piltover_Snowball_DisplayName",
      "GeneratedTip_SummonerSpell_Crepe_Zaun_Snowball_DisplayName",
    ];
    if (bridgeOfProgressSpells.includes(longSpellName)) {
      return spells?.SummonerSnowball;
    }
  }

  return spells[spellKey];
};

export const getChampionKeyById = (championId) => {
  const champions = getStaticData("champions");
  const championKey = champions?.keys[championId];
  return championKey;
};

export const getAbilityVideo = (championKey, hotkey) => {
  return `${appURLs.CDN_VIDEOS}/tooltip_videos/${championKey}/${hotkey}.webm`;
};

export const getCorrectAbilityImg = (championKey, abilityKey) => {
  const key = `${championKey}_${abilityKey}`;
  return CHAMPION_ABILITY_MAP[key];
};

export const getLaneWinRate = (stats) => {
  return stats.laneWins / (stats.games || 1);
};
export const sortByLaneWinRate = (arr?: any[], isAsc?: boolean) => {
  if (!arr) return [];
  if (!Array.isArray(arr)) return [];

  // process nested key
  const sorted = arr.sort((a, b) => {
    const v1 = getLaneWinRate(a);
    const v2 = getLaneWinRate(b);
    return v1 - v2;
  });
  return isAsc ? sorted : sorted.reverse();
};
export const sortByWinRate = (arr?: any[], isAsc?: boolean) => {
  if (!arr) return [];
  if (!Array.isArray(arr)) return [];

  // process nested key
  const sorted = arr.sort((a, b) => {
    const v1 = a.wins / (a.games || 1);
    const v2 = b.wins / (b.games || 1);
    return v1 - v2;
  });
  return isAsc ? sorted : sorted.reverse();
};

export function filterMatchupsByThreshold(
  championStats,
  matchupStats,
  matchupThreshold = 0.01,
) {
  if (!championStats && !matchupStats) return false;
  return (matchupStats?.games || 0) / (championStats?.games || 1) >=
    matchupThreshold
    ? 1
    : 0;
}

export function calcTeamDamages(teamStats) {
  let ap = 0;
  let ad = 0;
  let tr = 0;

  for (const championStats of teamStats || []) {
    if (!championStats.previous_patch_stats) continue;
    const {
      previous_patch_stats: {
        true_damage_dealt_to_champions,
        magic_damage_dealt_to_champions,
        physical_damage_dealt_to_champions,
      },
    } = championStats;
    tr += true_damage_dealt_to_champions / championStats.games || 1;
    ap += magic_damage_dealt_to_champions / championStats.games || 1;
    ad += physical_damage_dealt_to_champions / championStats.games || 1;
  }

  // To avoid cases where the total sum of percentages is > or < 100 we need some computations
  // First of all we need to sort and truncate all percentages (without rounding)

  const dmgSum = tr + ap + ad;
  const vals: [string, number][] = [
    ["tr", !tr ? 0 : (tr / dmgSum) * 100],
    ["ap", !ap ? 0 : (ap / dmgSum) * 100],
    ["ad", !ad ? 0 : (ad / dmgSum) * 100],
  ];
  let damageShare = vals.sort((a, b) => a[1] - b[1]);

  // Then calculate the difference between 100 and the actual sum
  const diff = 100 - damageShare.reduce((acc, v) => acc + ~~v[1], 0);

  // Then we distribute the diffs
  if (ad && ad && tr)
    damageShare = damageShare.map((v, i) => [
      v[0],
      i < diff ? ~~v[1] + 1 : ~~v[1],
    ]);

  const apDamageShare = damageShare.find((v) => v[0] === "ap")[1] || null;
  const adDamageShare = damageShare.find((v) => v[0] === "ad")[1] || null;
  const trueDamageShare = damageShare.find((v) => v[0] === "tr")[1] || null;

  return { ap, ad, tr, apDamageShare, adDamageShare, trueDamageShare };
}

export function determineSkillOrder(skills, threshold = 4, length = 3) {
  const counter = {};
  const result = [];

  if (!skills) return result;

  for (const skill of skills) {
    if (Number(skill) !== 0) {
      if (!(skill in counter)) counter[skill] = 0;

      counter[skill]++;

      if (counter[skill] === threshold) result.push(Number(skill));
    }
  }

  const counterArray = Object.entries(counter)
    .map(([spellID, count]) => ({
      spellID: Number(spellID),
      count,
    }))
    .filter((spell) => !result.includes(spell.spellID))
    .sort((a: any, z: any) => z.count - a.count);

  for (const incompleteSpell of counterArray) {
    result.push(incompleteSpell.spellID);
  }

  return result.slice(0, length);
}

export function aggregatePlayerChampions(stats) {
  const defaultStats = {
    total: {
      wins: 0,
      games: 0,
    },
  };

  if (!stats?.length || stats instanceof Error) return defaultStats;

  return stats.reduce((acc, curr) => {
    if (!curr?.basicStats) return acc;
    const { championId: id, gameCount, basicStats } = curr;
    const { wins, kills, deaths, assists } = basicStats;

    if (!acc[id])
      acc[id] = {
        championId: id,
        wins: 0,
        games: 0,
        kills: 0,
        deaths: 0,
        assists: 0,
      };

    acc.total.games += gameCount ?? 0;
    acc.total.wins += wins ?? 0;

    acc[id].games += gameCount ?? 0;
    acc[id].wins += wins ?? 0;
    acc[id].kills += kills ?? 0;
    acc[id].deaths += deaths ?? 0;
    acc[id].assists += assists ?? 0;

    return acc;
  }, defaultStats);
}

export const playerChampionStats = (stats = {}, championId = 0) => {
  const champStats = stats[championId] || {};
  return {
    ...champStats,
    wins: champStats.wins ?? 0,
    games: champStats.games ?? 0,
    kills: champStats.kills ?? 0,
    deaths: champStats.deaths ?? 0,
    assists: champStats.assists ?? 0,
    kda: champStats.games
      ? (champStats.kills + champStats.assists) / (champStats.deaths || 1)
      : 0,
  };
};

export function championProficiency(playStyles, championId) {
  if (!championId || !playStyles || playStyles instanceof Error) return null;

  // NOTE: this is *RECENT* counts (30 days/games)
  const counts = (playStyles?.championCounts || []).reduce(
    (acc, curr) => {
      acc[curr.id] = curr.count;
      acc.total += curr.count;
      return acc;
    },
    { total: 0 },
  );

  const count = counts[championId] ?? 0;

  switch (true) {
    case counts.total < 30:
      return null;
    case count >= 22:
      return CHAMPION_PROFICIENCY.otp;
    case count > 10:
      return CHAMPION_PROFICIENCY.lover;
    case count > 6:
      return CHAMPION_PROFICIENCY.fan;
    default:
      return null;
  }
}

export function championProficiencyTitle(championName, proficiency) {
  switch (proficiency) {
    case CHAMPION_PROFICIENCY.otp:
      return t(
        "lol:playStyleTags.tags.champion_counts.otp",
        "OTP {{championName}}",
        { championName },
      );
    case CHAMPION_PROFICIENCY.lover:
      return t(
        "lol:playStyleTags.tags.champion_counts.lover",
        "{{championName}} Lover",
        { championName },
      );
    case CHAMPION_PROFICIENCY.fan:
      return t(
        "lol:playStyleTags.tags.champion_counts.fan",
        "{{championName}} Fan",
        { championName },
      );
    default:
      return null;
  }
}

export function championProficiencyDescription(
  championName,
  proficiency,
  currChampionCount,
  totalGames,
) {
  switch (proficiency) {
    case CHAMPION_PROFICIENCY.otp:
      return t(
        "lol:playStyleTags.tips.champion_counts.otp",
        "Pretty much only plays {{championName}}. OTP = One Trick Pony.",
        {
          championName,
        },
      );
    case CHAMPION_PROFICIENCY.lover:
      return t(
        "lol:playStyleTags.tips.champion_counts.lover",
        "Over {{currChampionCount}} games of {{championName}} in the last {{totalGames}} games.",
        {
          championName,
          currChampionCount,
          totalGames,
        },
      );
    case CHAMPION_PROFICIENCY.fan:
      return t(
        "lol:playStyleTags.tips.champion_counts.lover",
        "Over {{currChampionCount}} games of {{championName}} in the last {{totalGames}} games.",
        {
          championName,
          currChampionCount,
          totalGames,
        },
      );
    default:
      return null;
  }
}

export function absoluteRank(tierSymbol, rank, lp) {
  if (!tierSymbol || !rank) return 0;
  const tier =
    typeof tierSymbol === "symbol"
      ? RANK_SYMBOL_TO_STR[tierSymbol].gql
      : tierSymbol;
  const key = `${tier}_${rank}`;
  return IS_APEX[tier]
    ? RANK_VALS.APEX + lp
    : RANK_VALS[key]
      ? RANK_VALS[key] + lp
      : 0;
}

export function getChampionLink(championKey, tab, matchupChampionKey, filters) {
  const pathname = `/${lolRefs.lolChampionPrefix}/${championKey}/${tab}/${
    matchupChampionKey || ""
  }`;
  const urlParams = getSearchParamsForChampions(filters);
  const paramString = urlParams?.toString();
  return paramString ? pathname + `?${paramString}` : pathname;
}

export function findRuneTree(runeId) {
  const patch = getCurrentPatchForStaticData();
  const lang = getLocale();
  const ritoLanguageCode = getRitoLanguageCodeFromBCP47(lang);
  const result =
    readState.lol.staticData[ritoLanguageCode]?.[patch]?.runeDetails;

  return result?.[runeId]?.tree;
}

/**
 * Builds rune trees from static data based on passed runes array
 *
 * Returns: an array of role symbols in the same order as the champions were
 *
 * @function
 * @param {Array} runes             Array of active runes ids
 * @param {Array} runesStaticData   Runes static data
 * @param {Function} selectRuneRow  function for selecting runes
 * @return {Object}
 */
export function getRuneTrees({
  runes = [],
  runesStats = {} as any,
  buildGames = 0,
  runesStaticData = [],
  selectRuneRow,
  selectRuneTree,
  selectRuneTreeSecondary,
  isProBuild = false,
}) {
  // There are 2 types of rune array:
  // length 11: [0] & [5] are the ids of the trees
  // length 9: trees excluded aka [0] is keystone
  const withTrees = runes.length === 11;
  const shardStart = withTrees ? 8 : 6;

  // Primary Tree
  const tree1 = withTrees ? runes[0] : findRuneTree(runes[0]);
  const runesPrimaryTree = runesStaticData.find(
    (tree) => tree.id === tree1,
  ) ?? {
    slots: [],
  };

  const runesPrimaryRows = runesPrimaryTree.slots.map((row, rowIndex) => {
    const rowStats = runesStats?.[rowIndex] || {};
    const isKeystone = rowIndex === 0;

    return {
      keystoneRow: isKeystone,
      row: row.runes.map((rune) => {
        return {
          ...rune,
          selectRune: selectRuneRow,
          image: Static.getRuneImage(rune.id),
          active: runes.includes(rune.id),
          isKeystone: isKeystone,
          statShardRow: rowIndex + 1,
          tooltip: RuneTooltip(
            rune.id,
            rowStats[rune.id],
            buildGames,
            isProBuild,
          ),
          stats: rowStats[rune.id],
          highlight: Boolean(rowStats[rune.id]),
        };
      }),
    };
  });
  const runeTreesLeft = runesStaticData.map((tree) => ({
    id: tree.id,
    image: Static.getRuneImage(tree.id),
    active: tree.id === tree1,
    selectTree: selectRuneTree,
  }));

  // Secondary Tree
  const tree2 = withTrees ? runes[5] : findRuneTree(runes[4]);
  const runesSecondaryTree = runesStaticData.find(
    (tree) => tree.id === tree2,
  ) ?? {
    slots: [],
  };
  const runesSecondaryRows = runesSecondaryTree.slots
    .filter((_, rowIndex) => rowIndex !== 0)
    .map((row) => {
      const rowStats = runesStats.secondary || {};

      return {
        row: row.runes.map((rune) => ({
          ...rune,
          selectRune: selectRuneRow,
          image: Static.getRuneImage(rune.id),
          active: runes.includes(rune.id),
          tooltip: RuneTooltip(
            rune.id,
            rowStats[rune.id],
            buildGames,
            isProBuild,
          ),
          stats: rowStats[rune.id],
          highlight: Boolean(rowStats[rune.id]),
        })),
      };
    });
  const runeTreesRight = runesStaticData
    .filter((tree) => tree.id !== tree1)
    .map((tree) => ({
      id: tree.id,
      image: Static.getRuneImage(tree.id),
      active: tree.id === tree2,
      selectTree: selectRuneTreeSecondary,
    }));

  // Shards
  // @ts-ignore
  const currentPatch = readState.lol.currentPatch?.short;
  const runeShards = shardMap(currentPatch).map((shardRow, rowIndex) => {
    const rowStats = runesStats?.[`shards${rowIndex + 1}`] || {};

    return {
      row: shardRow.map((shardId) => ({
        id: Number(shardId),
        statShardRow: rowIndex + 8,
        selectRune: selectRuneRow,
        image: Static.getRuneImage(shardId),
        active: runes[rowIndex + shardStart] === Number(shardId),
        tooltip: RuneTooltip(
          shardId,
          rowStats[shardId],
          buildGames,
          isProBuild,
        ),
        highlight: Boolean(rowStats[shardId]),
      })),
    };
  });

  return {
    runeTreesLeft,
    runesPrimaryTree,
    runeTreesRight,
    runesSecondaryTree,
    runesPrimaryRows,
    runesSecondaryRows,
    runeShards,
  };
}

export function isRemakeGame(matchDuration) {
  return durationToMinutes(matchDuration) <= 5;
}

export function isAbandonedMatch(match) {
  // No winner = only losers = abandoned game
  if (isArenaQueue(match?.queueId) || isSwarmQueue(match?.queueId)) {
    return false;
  }
  if (!match?.teams) return false;
  return !match.teams.find(({ win }) => win);
}

export function isMatchInQueue(match, queue) {
  return !match || !queue || QueueSymbol(match.queueId) === QueueSymbol(queue);
}

export function isSameAccount(account1, account2) {
  const ids = [
    "puuid",
    "accountId",
    "encryptedSummonerId",
    "unencryptedSummonerId",
    "derivedId",
    "summonerName",
  ];
  if (!account1 || !account2) {
    return false;
  }
  for (const id of ids) {
    let id1 = account1?.[id];
    let id2 = account2?.[id];
    if (id === "accountId" && !id1) id1 = account1?.["currentAccountId"];
    if (id === "accountId" && !id2) id2 = account2?.["currentAccountId"];
    if (id1 && id2 && id1 === id2) return true;
  }

  // Mostly on match pages the routes 'name' variable is just put into the summonerName field
  if (account2?.summonerName && isDerivedRiotID(account2.summonerName)) {
    const [gameName] = separateDerivedRiotID(account2.summonerName);
    if (gameName === account1.riotIdGameName) return true;
  }

  return false;
}

export function getChampion(keyOrId) {
  const champions = getStaticData("champions");
  const key = champions?.[keyOrId] ? keyOrId : champions?.keys?.[keyOrId];
  const champion = champions?.[key];
  if (!champion) return null;
  const id = champion?.key; // This is misleading because in the data, key = id here. blame rito
  return {
    key,
    id,
    name: champion?.name,
  };
}

export function getRuneArrayFromParticipant(participant) {
  const runeArray = [
    participant.perks.styles[0]?.style,
    participant.perks.styles[0]?.selections[0]?.perk,
    participant.perks.styles[0]?.selections[1]?.perk,
    participant.perks.styles[0]?.selections[2]?.perk,
    participant.perks.styles[0]?.selections[3]?.perk,
    participant.perks.styles[1]?.style,
    participant.perks.styles[1]?.selections[0]?.perk,
    participant.perks.styles[1]?.selections[1]?.perk,
  ];
  return runeArray;
}

export function getRuneTreeObjectFromRuneArray(runeArray, perks) {
  const treeObject = {} as any;
  if (!runeArray) return {};
  if (runeArray.indexOf(undefined) !== -1 || !Array.isArray(perks)) {
    // if no runes
    treeObject.runes = [];
    treeObject.mainTree = null;
    treeObject.mainTreeKey = null;
    treeObject.secondTree = null;
    treeObject.secondTreeKey = null;
    treeObject.keystone = null;
    return treeObject;
  }
  treeObject.runes = runeArray;
  treeObject.mainTree = perks.find((tree) => tree.id === runeArray[0]);
  treeObject.mainTreeKey = (treeObject.mainTree?.key ?? "").toLowerCase();
  treeObject.secondTree = perks.find((tree) => tree.id === runeArray[5]);
  treeObject.secondTreeKey = (treeObject.secondTree?.key ?? "").toLowerCase();
  if (treeObject.mainTree) {
    treeObject.keystone = treeObject.mainTree.slots[0].runes.find(
      (rune) => rune.id === runeArray[1],
    );
  }
  return treeObject;
}

export function getRuneTreeObjectFromParticipant(participant, perks) {
  const runeArray = getRuneArrayFromParticipant(participant);
  return getRuneTreeObjectFromRuneArray(runeArray, perks);
}

export const tierToAbbr = (league) => {
  switch (league.tier) {
    case "IRON":
    case RANK_SYMBOLS.iron:
      return "I";
    case "BRONZE":
    case RANK_SYMBOLS.bronze:
      return "B";
    case "SILVER":
    case RANK_SYMBOLS.silver:
      return "S";
    case "GOLD":
    case RANK_SYMBOLS.gold:
      return "G";
    case "EMERALD":
    case RANK_SYMBOLS.emerald:
      return "E";
    case "PLATINUM":
    case RANK_SYMBOLS.platinum:
      return "P";
    case "DIAMOND":
    case RANK_SYMBOLS.diamond:
      return "D";
    case "MASTER":
    case "GRANDMASTER":
    case "CHALLENGER":
    case RANK_SYMBOLS.master:
    case RANK_SYMBOLS.grandmaster:
    case RANK_SYMBOLS.challenger:
      return league.leaguePoints;
    default:
      return "-";
  }
};

export function format(stat) {
  let seconds = new Date(stat).getSeconds().toString();
  seconds = seconds.length === 1 ? seconds + "0" : seconds;

  return `${new Date(stat).getMinutes().toString()}:${seconds}`;
}

export const getSuppItemUpgradeTimes = (timeline, currParticipant) => {
  const scores = {
    tier1Time: 0,
    tier2Time: 0,
    itemId1: SUPPORT_ITEM.two,
    itemId2: SUPPORT_ITEM.three,
    fullyUpgraded: false,
    loading: true,
  };

  if (!timeline?.frames?.length) return scores;

  frames: for (const frame of timeline.frames) {
    for (const event of frame.events || []) {
      if (
        event.type !== "ITEM_DESTROYED" &&
        event.participantId === currParticipant?.participantId
      )
        continue;
      if (event.itemId === SUPPORT_ITEM.one) {
        // Tier 1 -> Tier 2
        scores.tier1Time = event.timestamp;
      } else if (event.itemId === SUPPORT_ITEM.two) {
        // Tier 2 -> Tier 3
        scores.tier2Time = event.timestamp;
        scores.fullyUpgraded = true;
        break frames;
      }
    }
  }

  scores.loading = false;

  return scores;
};

export function isCompletedItem(itemID = "", itemsStaticData = {}) {
  let isCompleted = false;

  const itemInfo = itemsStaticData[itemID];
  if (!itemInfo) return isCompleted;

  // Doesnt build INTO anything and has a depth
  if (itemInfo.gold?.total >= 1900) {
    isCompleted = true;
  }

  return isCompleted;
}

export function aggregateMatches(matches = [], itemsStaticData = {}) {
  const agg = {
    wins: 0,
    games: 0,
    keystones: {},
    starters: {},
    finalItems: {},
    skillOrders: {},
  };

  if (!matches || !itemsStaticData) {
    return agg;
  }

  for (const match of matches) {
    agg.games += 1;
    if (match.win) agg.wins += 1;

    // Aggregate skill orders
    const skills = match.skillOrder.map((level) => level.skillSlot);
    const skillOrder = determineSkillOrder(skills).map(
      (spell) => SKILL_HOTKEYS[spell - 1],
    );
    const skillOrderID = skillOrder.join(":");
    if (!agg.skillOrders[skillOrderID]) agg.skillOrders[skillOrderID] = 0;
    agg.skillOrders[skillOrderID] += 1;

    // Aggregate keystones
    const keystone = match.runes?.[0]?.id;
    if (!agg.keystones[keystone]) agg.keystones[keystone] = 0;
    agg.keystones[keystone] += 1;

    // Aggregate starting items
    const starters = match.buildPaths.filter(
      (p) =>
        p.timestamp < 30000 &&
        !TRINKETS[p.itemId] &&
        !POTIONS[p.itemId] &&
        !WARDS[p.itemId],
    );

    for (const { itemId } of starters) {
      if (!agg.starters[itemId]) agg.starters[itemId] = 0;
      agg.starters[itemId] += 1;
    }

    // Aggregate final items
    for (const item of match.items) {
      if (isCompletedItem(item.id, itemsStaticData)) {
        if (!agg.finalItems[item.id]) agg.finalItems[item.id] = 0;
        agg.finalItems[item.id] += 1;
      }
    }
  }

  return {
    winRate: Math.round((agg.wins / agg.games) * 100),
    skillOrders: Object.entries(agg.skillOrders)
      .sort((a: any, z: any) => z[1] - a[1])
      .map((item) => item[0]),
    keystones: Object.entries(agg.keystones)
      .sort((a: any, z: any) => z[1] - a[1])
      .map((item) => item[0]),
    starters: Object.entries(agg.starters)
      .map(([id, count]) => ({
        id,
        count,
      }))
      .sort((a: any, b: any) => b.count - a.count),
    items: Object.entries(agg.finalItems)
      .sort((a: any, z: any) => z[1] - a[1])
      .map((item) => item[0]),
  };
}

export function getCurrentSeason() {
  let currSeason = 0;

  for (const id in SEASONS_START) {
    const date = SEASONS_START[id];
    const now = new Date();

    if (now > date) {
      currSeason = Number(id);
    }
  }

  return currSeason;
}

export function regionsToServices(region: string | symbol = "world") {
  switch (typeof region === "symbol" ? region : String(region).toLowerCase()) {
    case REGION_SYMBOL_NA:
    case "na1":
    case "na":
      return "na1";
    case REGION_SYMBOL_EUNE:
    case "eun1":
    case "eune":
      return "eun1";
    case REGION_SYMBOL_EUW:
    case "euw1":
    case "euw":
      return "euw1";
    case REGION_SYMBOL_JP:
    case "jp1":
    case "jp":
      return "jp1";
    case REGION_SYMBOL_KR:
    case "kr":
      return "kr";
    case REGION_SYMBOL_LAN:
    case "la1":
    case "lan":
      return "la1";
    case REGION_SYMBOL_LAS:
    case "la2":
    case "las":
      return "la2";
    case REGION_SYMBOL_MEA:
    case "mea":
    case "me1":
      return "me1";
    case REGION_SYMBOL_BR:
    case "br1":
    case "br":
      return "br1";
    case REGION_SYMBOL_OCE:
    case "oc1":
    case "oce":
      return "oc1";
    case REGION_SYMBOL_TR:
    case "tr1":
    case "tr":
      return "tr1";
    case REGION_SYMBOL_RU:
    case "ru":
      return "ru";
    case REGION_SYMBOL_SG:
    case "sg2":
      return "sg2";
    case REGION_SYMBOL_PH:
    case "ph":
    case "ph2":
      return "ph2";
    case REGION_SYMBOL_VN:
    case "vn":
    case "vn2":
      return "vn2";
    case REGION_SYMBOL_TH:
    case "th":
    case "th2":
      return "th2";
    case REGION_SYMBOL_TW:
    case "tw":
    case "tw2":
      return "tw2";
    default:
      return String(region).toLowerCase();
  }
}

export function isPBE(region = "") {
  return region?.toLowerCase() === "pbe1";
}

export function calcKDA(kills, deaths, assists, fraction = 1) {
  return calcRate(kills + assists, deaths, fraction);
}

const lolEndpointRegex = new RegExp("(.*://.*:.+?/|.*://)(.*)");

export const getLolEndpointKey = (url) => {
  const endpoint = lolEndpointRegex.exec(url)[2];
  return endpoint;
};

export const getGoldDiff = (frames = [], t1, participants, myTeam) => {
  let blueSum;
  let redSum;

  return frames.map((frame) => {
    const { participantFrames, timestamp } = frame;
    blueSum = 0;
    redSum = 0;

    for (let i = 1; i <= participants; i++) {
      if (i <= t1 && myTeam === 200) {
        redSum += participantFrames[i]?.totalGold;
      } else if (i <= t1 && myTeam !== 200) {
        blueSum += participantFrames[i]?.totalGold;
      } else if (i > t1 && myTeam === 200) {
        blueSum += participantFrames[i]?.totalGold;
      } else {
        redSum += participantFrames[i]?.totalGold;
      }
    }

    return { diff: blueSum - redSum, time: timestamp };
  });
};

export const kFormatter = (num) => {
  return num > 999 ? `${(num / 1000).toFixed(1)}k` : num;
};

export const getDamageTextPercentage = (percent, type) => {
  const textTreshold = 0.4;

  return percent >= textTreshold
    ? t(`lol:amount${type}`, `{{amount}}% ${type}`, {
        amount: (percent * 100).toFixed(0),
      })
    : t("lol:amount", "{{amount}}%", {
        amount: (percent * 100).toFixed(0),
      });
};

export function damageBreakdownUI(damageBreakdown) {
  const {
    apDamageShare = 0,
    adDamageShare = 0,
    trueDamageShare = 0,
  } = damageBreakdown;

  const ALERT_THRESHOLD = 0.6;

  return {
    alert: apDamageShare >= ALERT_THRESHOLD || adDamageShare >= ALERT_THRESHOLD,
    apColor: apDamageShare ? "var(--ap)" : "var(--shade3)",
    adColor: adDamageShare ? "var(--ad)" : "var(--shade3)",
    trueColor: trueDamageShare ? "var(--true)" : "var(--shade3)",
    adPercentText: adDamageShare
      ? getDamageTextPercentage(adDamageShare, "AD")
      : "-",
    apPercentText: apDamageShare
      ? getDamageTextPercentage(apDamageShare, "AP")
      : "-",
    truePercentText: trueDamageShare
      ? getDamageTextPercentage(trueDamageShare, "True")
      : "-",
  };
}

export function calculateTeamDamages(team = [], championsReport) {
  let apPercent = 0;
  let adPercent = 0;
  let trPercent = 0;

  let apTotal = 0;
  let adTotal = 0;
  let trueTotal = 0;

  if (!championsReport) {
    return {
      apDamageShare: apPercent,
      adDamageShare: adPercent,
      trueDamageShare: trPercent,
    };
  }

  const validTeam = team.filter(
    (p) => Boolean(p?.championId) && p.championId > 0,
  );

  for (const player of validTeam) {
    let championReport = [...championsReport].find((c) => {
      const matchesChampion = c.championId === player.championId;
      const matchesRole = isRoleQueue(c.queue_id)
        ? RoleSymbol(c.individual_position) === RoleSymbol(player.role)
        : RoleSymbol(c.individual_position) === ROLE_SYMBOLS.all;
      return matchesChampion && matchesRole;
    });

    if (!championReport) {
      championReport = [...championsReport]
        .filter((c) => c.championId === player.championId)
        .sort((a, b) => b.games - a.games)[0];
    }
    if (!championReport?.postmatch_stats) continue;

    const {
      games = 1,
      postmatch_stats: {
        true_damage_dealt_to_champions = 0,
        magic_damage_dealt_to_champions = 0,
        physical_damage_dealt_to_champions = 0,
      },
    } = championReport;

    if (
      magic_damage_dealt_to_champions > 0 &&
      physical_damage_dealt_to_champions > 0 &&
      true_damage_dealt_to_champions > 0
    ) {
      apTotal += magic_damage_dealt_to_champions / games;
      adTotal += physical_damage_dealt_to_champions / games;
      trueTotal += true_damage_dealt_to_champions / games;
    }
  }

  const damageTotal = trueTotal + apTotal + adTotal;
  apPercent = apTotal / (damageTotal || 1);
  adPercent = adTotal / (damageTotal || 1);
  trPercent = trueTotal / (damageTotal || 1);

  return {
    apDamageShare: apPercent,
    adDamageShare: adPercent,
    trueDamageShare: trPercent,
  };
}

export function groupRecentlyPlayedTeam(data) {
  const namesOnTeam = data.map((p) => p.name).sort();

  const players = {};
  for (const item of data) {
    players[item.name] = new Set(
      item.recentlyPlayedWith.filter((r) => namesOnTeam.includes(r)).sort(),
    );
  }

  const groups = new Set();

  function addToGroup(group, names) {
    if (!names.length) {
      return groups.add(group.sort().join("."));
    }

    for (const name of names) {
      addToGroup(
        [...group, name],
        names.filter((n) => players[n].has(name)),
      );
    }
  }

  addToGroup([], namesOnTeam);

  return new Array(...groups).map((v: string) => v.split("."));
}

export function determineFlashPlacement({
  manualFlashPlacement,
  defaultFlashPlacement,
}) {
  // If the manual flash placement setting is applied via blitz ui
  // it should take precident over the automatic/default value
  return manualFlashPlacement !== FLASH_PLACEMENT_NULL
    ? manualFlashPlacement
    : defaultFlashPlacement;
}

export function getTranslatedQueueName(t, queue) {
  const obj = getQueueDetails(queue || QUEUE_SYMBOLS.custom);
  const { label } = obj;
  return t(...label);
}

export function proFame(fame) {
  return fame >= 5 ? "star" : fame >= 3 ? "famous" : null;
}

export function playstylePremades(playersData = []) {
  const playersInLobby = playersData.map((p) => p.name);
  const players = playersData.map((player) => {
    return {
      ...player,
      recentlyPlayedWith: (player.recentlyPlayedWith || []).filter((p) =>
        playersInLobby.includes(p),
      ),
    };
  });

  const parties = {};

  let currPremadesId = 1;
  for (const player of players) {
    const friends = player.recentlyPlayedWith || [];
    const key = [player.name, ...friends]
      .sort((a, b) => a.localeCompare(b))
      .join("_");
    if (!parties[key]) {
      parties[key] = currPremadesId;
      currPremadesId += 1;
    }
  }

  for (const player of players) {
    for (const [partyKey, partyId] of Object.entries(parties)) {
      const party = partyKey.split("_");
      const friends = player.recentlyPlayedWith;

      const playerIsInParty = party.includes(player.name);
      const friendIsInParty = party.some((p) => friends.includes(p));

      if (!playerIsInParty && friendIsInParty) {
        delete parties[partyKey];
        const key = [...party, player.name]
          .sort((a, b) => a.localeCompare(b))
          .join("_");
        parties[key] = partyId;
      }
    }
  }

  const premades = {};

  for (const [partyKey, partyId] of Object.entries(parties)) {
    const party = partyKey.split("_");

    for (const player of party) {
      if (!premades[player] || party.length > premades[player].partySize) {
        premades[player] = {
          partyId,
          party,
          partySize: party.length,
        };
      }
    }
  }

  return premades;
}

export function getARAMStats(playerRanks, aramData) {
  const aramAgg = { wins: 0, losses: 0, games: 0, latest: 0 };

  if (aramData instanceof Error) {
    return {
      isARAMPlayer: false,
      stats: aramAgg,
    };
  }

  const soloQueueStats = (playerRanks || []).find(
    (rank) => QueueSymbol(rank.queue) === QUEUE_SYMBOLS.rankedSoloDuo,
  );
  const {
    wins: rankedWins = 0,
    losses: rankedLosses = 0,
    // insertedAt: latestRanked,
  } = soloQueueStats || {};
  const rankedGames = rankedWins + rankedLosses;

  const aramStats = (aramData || []).reduce((acc, champ) => {
    acc.wins += champ.basicStats.wins || 0;
    acc.losses += champ.gameCount - champ.basicStats.wins || 0;
    acc.games += champ.gameCount || 0;

    const lastPlayed = new Date(champ.lastPlayed).getTime();
    if (lastPlayed > acc.latest) {
      acc.latest = lastPlayed;
    }
    return acc;
  }, aramAgg);

  if (rankedGames >= 3) {
    return {
      isARAMPlayer: false,
      stats: aramStats,
    };
  }

  return {
    isARAMPlayer: aramStats.games / 5 >= rankedGames,
    stats: aramStats,
  };
}
